import {Injectable} from '@angular/core';
import {BaseApiService} from '../base-api.service';

/**
 * A project's history on the server: the restore points taken before Clear Map,
 * imports and restores, the nightly backups, and putting one back.
 *
 * All of it goes through the API rather than Storage. The copies are written
 * and read by the server only, which checks that the caller works on the
 * project; the browser used to list the bucket and download backups from
 * public links, and wiped and re-saved the project itself when restoring.
 */

export type BackupTrigger = 'clear' | 'import' | 'restore' | 'manual' | 'daily';

export interface BackupCounts {
  areas: number;
  lines: number;
  spots: number;
  groups: number;
  layers: number;
  /** Absent from copies made before comments were kept. */
  comments?: number;
}

export interface ProjectBackup {
  /** The server's name for the copy; passed back to restore it, never shown. */
  id: string;
  kind: 'daily' | 'restore_point';
  trigger: BackupTrigger;
  createdOn: number;
  createdBy: string;
  createdByName: string;
  counts: BackupCounts | null;
  /** For a Clear Map restore point: what was about to be removed. */
  cleared: BackupCounts | null;
  /** For a restore's own restore point: when the version then restored was made. */
  restoredFrom: number | null;
}

export interface ProjectBackupList {
  backups: ProjectBackup[];
  retention: {dailyBackups: number, restorePoints: number, maxAgeDays: number};
}

export interface RestoreResult {
  restored: BackupCounts;
  missingLayers: number;
  /** The restore point of the map just before, which undoes the restore. */
  undoBackup: ProjectBackup;
  restoredFrom: number;
}

@Injectable({providedIn: 'root'})
export class ProjectBackupsApiService extends BaseApiService {

  list(projectId: string): Promise<ProjectBackupList> {
    return this.get<ProjectBackupList>(`/projects/${encodeURIComponent(projectId)}/backups`);
  }

  /**
   * Takes a restore point of the project as the server has it. Flush pending
   * saves first, or the newest edits are not in it.
   */
  create(projectId: string, trigger: 'clear' | 'import' | 'manual', cleared?: BackupCounts): Promise<ProjectBackup> {
    return this.post<ProjectBackup>(`/projects/${encodeURIComponent(projectId)}/backups`, {trigger, cleared});
  }

  restore(projectId: string, backupId: string): Promise<RestoreResult> {
    return this.post<RestoreResult>(`/projects/${encodeURIComponent(projectId)}/backups/restore`, {backupId});
  }
}
